import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import HomePage from "@/pages/HomePage";
import ListingsPage from "@/pages/ListingsPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import MapPage from "@/pages/MapPage";
import ChiSiamoPage from "@/pages/ChiSiamoPage";
import ContattiPage from "@/pages/ContattiPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import FaqPage from "@/pages/FaqPage";
import AdminPage from "@/pages/AdminPage";

const ScrollManager = ({ lenisRef }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true, force: true });
    }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true, lerp: 0.12 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <ScrollManager lenisRef={lenisRef} />
      {!isAdmin && <SiteNav />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/immobili" element={<ListingsPage />} />
        <Route path="/immobili/:id" element={<PropertyDetailPage />} />
        <Route path="/mappa" element={<MapPage />} />
        <Route path="/chi-siamo" element={<ChiSiamoPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:id" element={<BlogPostPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contatti" element={<ContattiPage />} />
        <Route path="/admin" element={<AdminPage />} />
        {/* Redirect dai vecchi indirizzi del sito precedente, ancora indicizzati da Google */}
        <Route path="/property-category/vendita" element={<Navigate to="/immobili?contract=vendita" replace />} />
        <Route path="/property-category/affitto" element={<Navigate to="/immobili?contract=affitto" replace />} />
        <Route path="/property-category/*" element={<Navigate to="/immobili" replace />} />
        <Route path="/property" element={<Navigate to="/immobili" replace />} />
        <Route path="/property/*" element={<Navigate to="/immobili" replace />} />
        <Route path="/cerca-sulla-mappa" element={<Navigate to="/mappa" replace />} />
        <Route path="/vendita" element={<Navigate to="/immobili?contract=vendita" replace />} />
        <Route path="/affitto" element={<Navigate to="/immobili?contract=affitto" replace />} />
        {/* Qualsiasi altro vecchio indirizzo non previsto porta alla home (mai pagina bianca) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isAdmin && <SiteFooter />}
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#000000", color: "#FAFAF7", border: "1px solid #000000" } }} />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
